import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import styles from './MenuLinkList.module.css';
import { links } from 'config/admin/menu';
import { getUserPermissionLevel } from 'services/auth/tokenService';
import { useSelector } from 'react-redux';
import { groupingBasisOf, groupWords } from 'utils/groupLabel';
import { clubFeaturesOf, organisationTitleOf } from 'utils/clubFeatures';
import { teamWordsOf } from 'utils/teamLabel';

const MenuLinkList = ({ closeMenu }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const userPermissionLevel = getUserPermissionLevel();
  const { clubRecordsPendingCount, selectedClub } = useSelector(
    (state) => state.user || 0
  );

  // A menu entry with a groupWord is named in the selected club's own words
  // ("Age Groups", or "Year Groups" for a club that groups by year level).
  // One with a teamWord is named in the club's word for its teams ("Teams",
  // "Houses" or "Factions"), followed by its teamWordAfter if it has one
  // ("House Points"). One with an organisationWordAfter follows the club's
  // own word ("Club Records" or "School Records").
  const words = groupWords(groupingBasisOf(selectedClub));
  const teamWordsForClub = teamWordsOf(selectedClub);
  const menuName = (link) => {
    if (link.groupWord) return words[link.groupWord];
    if (link.teamWord) return `${teamWordsForClub[link.teamWord]}${link.teamWordAfter || ''}`;
    if (link.organisationWordAfter) return `${organisationTitleOf(selectedClub)}${link.organisationWordAfter}`;
    return link.name;
  };

  // An entry is shown when the user's level reaches it and, where it names a
  // club feature, the selected club has that feature. The same test is made
  // for a group and for each entry inside it, and a group left with nothing
  // inside it is not shown. It is a comparison of values already in memory:
  // no request is made.
  const features = clubFeaturesOf(selectedClub);
  const isShown = (link) =>
    (!link.permissionLevel || link.permissionLevel <= userPermissionLevel) &&
    (!link.feature || features[link.feature]);
  const shownChildren = (link) => (link.child || []).filter(isShown);
  const isGroupShown = (link) =>
    isShown(link) && (!link.child?.length || shownChildren(link).length > 0);

  // Track expanded parent menu
  const [expanded, setExpanded] = useState(null);

  const toggleExpand = (index) => {
    setExpanded(expanded === index ? null : index);
  };

  return (
    <>
      {links.map(
        (link, index) =>
          isGroupShown(link) && (
            <div key={index} className={styles.sidebarNavGroup}>
              {/* Parent button */}
              {!link.child?.length ? (
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    navigate(link.url);
                    closeMenu();
                  }}
                  className={`${styles.sidebarNavItem} ${
                    location.pathname === link.url ? styles.active : ''
                  }`}
                >
                  <span className={styles.sidebarNavIcon}>{link.icon}</span>
                  {menuName(link)}
                  {link.pending && clubRecordsPendingCount > 0 && (
                    <div className={styles.badge}>
                      {clubRecordsPendingCount} new
                    </div>
                  )}
                </button>
              ) : (
                <>
                  <button
                    onClick={() => toggleExpand(index)}
                    className={`${styles.sidebarNavItem} ${
                      expanded === index ? styles.expanded : ''
                    }`}
                  >
                    <span className={styles.sidebarNavIcon}>{link.icon}</span>
                    {menuName(link)}
                    <span className={styles.expandArrow}>
                      {expanded === index ? '▾' : '▸'}
                    </span>
                  </button>

                  {/* Child links */}
                  {expanded === index && (
                    <div className={styles.childMenu}>
                      {shownChildren(link).map((childLink, childIndex) => (
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(childLink.url);
                            closeMenu();
                          }}
                          key={childIndex}
                          className={`${styles.sidebarNavItem} ${styles.childItem} ${
                            location.pathname === childLink.url
                              ? styles.active
                              : ''
                          }`}
                        >
                          <span className={styles.sidebarNavIcon}>
                            {childLink.icon}
                          </span>
                          {menuName(childLink)}
                          {childLink.pending && clubRecordsPendingCount > 0 && (
                            <div className={styles.badge}>
                              {clubRecordsPendingCount} new
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )
      )}
    </>
  );
};

MenuLinkList.propTypes = {
  closeMenu: PropTypes.func.isRequired,
};

export default MenuLinkList;
